
import Vue from 'vue'
import { Candidate, Month, UserProfile } from 'vue/types/vue'
import { startCandidateContact } from '@/helpers'
import moment from 'moment'
import BackLink from '@/components/atoms/BackLink.vue'
import DateIcon from '@/assets/svgs/date_icon.svg?inline'
import EducationIcon from '@/assets/svgs/education_icon.svg?inline'
import LanguageIcon from '@/assets/svgs/language_icon.svg?inline'
import LocationIcon from '@/assets/svgs/location_icon.svg?inline'
import Person from '@/assets/svgs/person.svg?inline'
import ScheduleIcon from '@/assets/svgs/schedule_icon.svg?inline'
import WorkIcon from '@/assets/svgs/work_icon.svg?inline'
import { GET_PUBLIC_PROFILE_INFO } from '@/graphql'
import ISO from 'iso-639-1'
import STATE_ISO_MAP from '@/plugins/states'
import MONTH_ENUM_MAP from '@/plugins/months'
import DEGREE_ENUM_MAP from '@/plugins/degrees'

const dateFormat = 'MMM YYYY'

interface Data {
  candidate?: Candidate
}

interface Methods {
  transformQueryResponseUser:(user: UserProfile) => Candidate
  handleCandidateContact: () => void
  getWorkExperienceDatesText: (
    startMonth: Month,
    startYear: string,
    endMonth: Month | null,
    endYear: string | null
  ) => string
  backToEdit: () => void
}

interface Computed {}

interface Props {
  showPreview: boolean
  previousRouteName?: string
}

export default Vue.extend<Data, Methods, Computed, Props>({
  name: 'CandidateProfile',
  components: {
    BackLink,
    EducationIcon,
    DateIcon,
    WorkIcon,
    LocationIcon,
    LanguageIcon,
    Person,
    ScheduleIcon,
  },
  props: {
    showPreview: {
      type: Boolean,
      required: false,
      default: false
    },
    previousRouteName: {
      type: String,
      required: false,
    },
  },
  apollo: {
    candidate: {
      query: GET_PUBLIC_PROFILE_INFO,
      variables: function () {
        if (this.$route.params?.masqueradeUser) {
          return { uuid: this.$route.params?.masqueradeUser }
        }
      },
      update (data) {
        return this.transformQueryResponseUser(data.user as UserProfile)
      },
    }
  },
  methods: {
    transformQueryResponseUser (user: UserProfile) {
      const { uuid, avatarUrl, currentProgram, bio, firstName, middleName, lastName, languages, career, educations, workExperiences } = user

      const candidate: Candidate = {
        id: uuid,
        bio: bio ?? '',
        // Set profile picture url
        avatarUrl: avatarUrl
          ? `${process.env.VUE_APP_FILE_STORAGE_PATH}/${avatarUrl}`
          : null,

        // Concatenate user's name
        name: [firstName, middleName, lastName].join(' '),

        // User's availability
        availability: [
            career?.isFullTime && 'Full-time',
            career?.isPartTime && 'Part-time',
          // TODO: Determine fallback value
        ].filter(s => !!s).join(', ') || 'n/a',

        estimatedCompletion: career?.estimatedProgramCompletionDate
          ? moment(career.estimatedProgramCompletionDate).format('MMM YYYY')
          : 'n/a',

        currentProgram: currentProgram,

        workExperience: (workExperiences || []).map(({
          id,
          title,
          companyName,
          city,
          stateIsoCode,
          startMonth,
          startYear,
          endMonth,
          endYear,
          jobDescription,
        }) => ({
          id: id!,
          title,
          companyName,
          jobDescription: jobDescription ?? '',
          location: `${city}, ${STATE_ISO_MAP[stateIsoCode]}`,
          formattedDate: this.getWorkExperienceDatesText(
            startMonth,
            startYear,
            endMonth,
            endYear,
          ),
        })),
        educationExperience: (educations || []).map(ed => ({
          id: ed.id!,
          type: DEGREE_ENUM_MAP[ed.degree],
          schoolName: ed.institution,
          location: `${ed.city}, ${STATE_ISO_MAP[ed.stateIsoCode]}`,
          year: ed.graduationYear,
        })),

        languages: (languages || []).map(lang => ISO.getName(lang)),
        location: career?.location?.city && career?.location?.stateIsoCode ? `${career.location.city}, ${STATE_ISO_MAP[career.location.stateIsoCode]}` : null

      }

      return candidate
    },
    handleCandidateContact () {
      // TODO: uncomment parameter when startCandidateContact is functional
      startCandidateContact(/* this.candidate!.id */)
    },
    getWorkExperienceDatesText (startMonth: Month, startYear: string, endMonth: Month | null, endYear: string | null) {
      const startMoment = moment(`${startYear} ${MONTH_ENUM_MAP[startMonth]}`, 'YYYY MMM')
      const endMoment = endMonth && endYear
        ? moment(`${endYear} ${MONTH_ENUM_MAP[endMonth]}`, 'YYYY MMM')
        : moment()

      const endFormat = endMonth && endYear ? endMoment.format(dateFormat) : 'Current'
      const startFormat = startMoment.format(dateFormat)

      const durationMonths = moment(endMoment).diff(startMoment, 'months') + 1 // Add one to make beginning month inclusive
      const months = durationMonths % 12
      const years = Math.floor(durationMonths / 12)

      const yearsText = years ? `${years} yr` : ''
      const monthsText = months ? `${months} mos` : ''

      const durationText = `${yearsText} ${monthsText}`.trim()

      return `${startFormat} - ${endFormat} (${durationText})`
    },
    backToEdit () {
      const routeName = this.previousRouteName ? this.previousRouteName : this.$route.params.masqueradeUser ? 'Masquerade Profile' : 'Profile'
      this.$router.push({ name: routeName, query: this.$route.query })
    }
  }
})
